/**
 * /join and /signin — the decisions of the account pages that are not the feed
 * (src/components/JoinPage.tsx), kept pure so they are tested without a browser.
 *
 * Creating an account and signing in are pages of their own: every door to either, on the
 * landing page, the catalogue and inside the feed (the top bar, the + sheet, comments), leads
 * here rather than opening a sheet over whatever card the feed started on. The feed keeps only
 * the views that belong to an account it already has: the profile, and "check your inbox".
 *
 * Which view a visitor lands on follows from who they already are, read without minting a
 * session: an account with a handle is done, an account without one picks it, an address
 * waiting for its link is told to open it, and everyone else gets the form — sign-in on /signin
 * (or /join?signin, the older spelling), create on /join. A forgotten password is asked for from
 * /signin and its emailed link lands on /reset, signed in, to choose a new one.
 *
 * Attribution passes through, as it does on the landing page: these pages sit between the
 * landing (or a pilot's link, or the feed itself) and the feed, so their doors into the feed
 * carry the ?via= and ?r= they arrived with, and ?back= — the card the person was on — so
 * finishing puts them back on it. Each value must pass the app's own check (src/lib/share.ts:
 * VIA, USER_ID, SLUG) or it is dropped, exactly as the feed would drop it.
 */
import type { Account } from '../api/account';

export type JoinView = 'create' | 'signin' | 'forgot' | 'reset' | 'confirm' | 'handle' | 'done';

export function joinViewFor(a: Account, search: string, pathname = '/join'): JoinView {
  // /reset is where the forgotten-password link lands, signed in: choose the new password. Without
  // a session the link did not work (expired, used, opened elsewhere): ask for a new one.
  if (pathname.replace(/\/+$/, '') === '/reset') return a.isAccount ? 'reset' : 'forgot';
  if (a.isAccount) return a.handle ? 'done' : 'handle';
  if (a.pendingEmail) return 'confirm';
  const signin = pathname.replace(/\/+$/, '') === '/signin' || new URLSearchParams(search).has('signin');
  return signin ? 'signin' : 'create';
}

/** The page each form lives on. */
export const PAGE_FOR: Record<'create' | 'signin', string> = { create: '/join', signin: '/signin' };

const VIA = /^[a-z0-9_-]{1,32}$/;
const USER_ID = /^[a-z0-9][a-z0-9_-]{0,80}$/i;
const BACK = /^\/c\/[a-z0-9][a-z0-9-]{0,80}$/i;

/** Only a card's own link or the feed: never another origin, never another page of ours. */
function backPath(raw: string | null): string | null {
  if (!raw) return null;
  return raw === '/feed' || BACK.test(raw) ? raw : null;
}

/** A door into the feed from these pages: the card they came from (or /feed) with `extra`, plus valid ?via= and ?r=. */
export function feedDoor(search: string, extra: Record<string, string> = {}): string {
  const q = new URLSearchParams(search);
  const out = new URLSearchParams(extra);
  const via = q.get('via');
  const r = q.get('r');
  if (via && VIA.test(via)) out.set('via', via);
  if (r && USER_ID.test(r)) out.set('r', r);
  const s = out.toString();
  const path = backPath(q.get('back')) ?? '/feed';
  return s ? `${path}?${s}` : path;
}

/**
 * The address of an account page, from wherever the door is: the feed passes the visit's own
 * attribution and the card it is on; the /feed?signup=1 and ?signin=1 links from before these
 * pages existed pass their query. `why` is a one-word reason the page says a sentence for.
 */
export function accountPageHref(view: 'create' | 'signin', from: { via?: string | null; r?: string | null; back?: string | null; why?: 'link' } = {}): string {
  const out = new URLSearchParams();
  if (from.via && VIA.test(from.via)) out.set('via', from.via);
  if (from.r && USER_ID.test(from.r)) out.set('r', from.r);
  const back = backPath(from.back ?? null);
  if (back && back !== '/feed') out.set('back', back);
  if (from.why) out.set('why', from.why);
  const s = out.toString();
  return s ? `${PAGE_FOR[view]}?${s}` : PAGE_FOR[view];
}

/** /feed?signup=1 or ?signin=1 (the old doors): where to send it instead, before the feed mounts. Null for anything else. */
export function accountRedirect(loc: { pathname: string; search: string }): string | null {
  if (loc.pathname.replace(/\/+$/, '') !== '/feed') return null;
  const q = new URLSearchParams(loc.search);
  const view = q.has('signup') ? 'create' : q.has('signin') ? 'signin' : null;
  return view ? accountPageHref(view, { via: q.get('via'), r: q.get('r') }) : null;
}
